import { Icon } from "@blueprintjs/core"
import { HeadlessService } from "@novu/headless"
import { Inbox, Notification as NovuNotification } from "@novu/react"
import React, { useEffect, useRef, useState } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"

import styles from "./Notifications.module.css"

import { NotificationsQuery } from "./__generated__/NotificationsQuery.graphql"

import { track } from "~/helpers/analytics"

import { parseMentionsInHtml } from "~/Notes/utils/mentions"
import { FallBackErrorBoundary } from "~/ProjectPlanner/FallbackErrorBoundry"
import { withRelayPageContainerNoLoading } from "~/RelayPageContainer"

type Props = {
  subscriberId: string | null
  subscriberHash: string | null
  appIdentifier: string
  backendUrl: string | null
}

type State =
  | {
      tag: "loading"
    }
  | {
      tag: "not-available"
    }
  | {
      tag: "not-enabled"
    }
  | {
      tag: "enabled"
    }

const QUERY = graphql`
  query NotificationsQuery {
    notification_types {
      id
    }
    current_account {
      id
      user_accounts {
        id
        user {
          id
          first_name
          last_name
          account_id
          email
        }
      }
    }
  }
`

const Notifications = (props: Props) => {
  const data = useLazyLoadQuery<NotificationsQuery>(QUERY, {})
  const inboxRef = useRef<HTMLDivElement>(null)

  const { subscriberId, appIdentifier, backendUrl, subscriberHash } = props

  const [state, setState] = React.useState<State>({ tag: "loading" })
  const [isInboxOpen, setIsInboxOpen] = useState(false)

  const hasNotificationsEnabled = data.notification_types.length > 0
  const users = data.current_account.user_accounts.map((ua) => ua.user)

  React.useEffect(() => {
    if (
      !subscriberId ||
      !subscriberHash ||
      !appIdentifier ||
      !backendUrl ||
      !hasNotificationsEnabled
    ) {
      setState({
        tag: "not-available",
      })
    } else {
      const headlessService = new HeadlessService({
        applicationIdentifier: appIdentifier,
        subscriberId,
        subscriberHash,
        backendUrl,
      })

      headlessService.initializeSession({
        listener: () => void 0,
        onSuccess: () => {
          headlessService.fetchUserPreferences({
            onSuccess: (settings) => {
              const isInAppChannelEnabled = settings.some(
                (setting) => setting.preference.channels.in_app,
              )

              if (!isInAppChannelEnabled) {
                setState({ tag: "not-enabled" })
              } else {
                setState({ tag: "enabled" })
              }
            },
            listener: () => void 0,
            onError: (error) => {
              console.error(
                "Failed to load notifications user preferences",
                error,
              )

              // Render the bell anyway
              setState({ tag: "enabled" })
            },
          })
        },
        onError: (error) => {
          console.error("Failed to initialize session", error)
          setState({ tag: "not-available" })
        },
      })
    }
  }, [
    subscriberId,
    appIdentifier,
    backendUrl,
    subscriberHash,
    hasNotificationsEnabled,
  ])

  React.useEffect(() => {
    const handleNotificationClick = (evt: Event) => {
      try {
        const notification = evt.currentTarget as HTMLElement
        const link = notification.querySelector(".hidden-redirect")

        if (!(link instanceof HTMLAnchorElement)) {
          return
        }
        const href = link.href

        const target = evt.target as HTMLElement
        const isNovuButton = target.classList.contains("nv-button")

        if (href && isNovuButton === false) {
          window.location.href = href
        }
      } catch (error) {
        console.error("Error while handling notification click", error)
      }
    }

    const addClickListeners = () => {
      document.querySelectorAll(".nv-notification").forEach((notification) => {
        notification.addEventListener("click", handleNotificationClick)
      })
    }

    const observer = new MutationObserver((mutations) => {
      // Only proceed if the notification list exists in the DOM
      if (!document.querySelector(".nv-notificationList")) return

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          addClickListeners()
        }
      })
    })

    if (isInboxOpen) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      observer.disconnect()
      document.querySelectorAll(".nv-notification").forEach((notification) => {
        notification.removeEventListener("click", handleNotificationClick)
      })
    }
  }, [isInboxOpen])

  React.useEffect(() => {
    if (isInboxOpen && state.tag === "enabled") {
      track("Notification Center Opened")
    }
  }, [isInboxOpen, state.tag])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isInboxOpen &&
        inboxRef.current &&
        !inboxRef.current.contains(event.target as Node)
      ) {
        setIsInboxOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isInboxOpen])

  function handleOnNotificationClick(notification: NovuNotification) {
    const identifier = notification?.workflow?.identifier ?? ""

    if (identifier.includes("timesheet-not-completed")) {
      track("Notification Clicked", {
        notification_type: "timesheet_incomplete",
      })
    }

    if (identifier.includes("resource-requests-updates")) {
      track("Notification Clicked", {
        notification_type: "resource_requests_updates",
      })
    }

    if (identifier.includes("user-resource-requests")) {
      track("Notification Clicked", {
        notification_type: "user_resource_requests",
      })
    }

    if (identifier.includes("at-mentions")) {
      track("Notification Clicked", {
        notification_type: "@mentions",
      })
    }
  }

  if (state.tag === "loading") {
    return null
  }

  if (state.tag === "not-available") {
    console.warn(
      "src/containers/Notifications.tsx component is missing subscriberId, appIdentifier or backendUrl. Skipping rendering.",
    )

    return null
  }

  if (state.tag === "not-enabled") {
    return null
  }

  const handleBellClick = () => {
    setIsInboxOpen(!isInboxOpen)
  }

  const parseMentions = (notification: NovuNotification): string => {
    return parseMentionsInHtml(notification.body, users)
  }

  return (
    <div ref={inboxRef}>
      <Inbox
        applicationIdentifier={appIdentifier}
        backendUrl={backendUrl}
        subscriberId={subscriberId}
        subscriberHash={subscriberHash}
        placement="bottom-end"
        localization={{
          "inbox.filters.labels.default": "Notifications",
        }}
        appearance={{
          elements: {
            preferences__button: styles.preferencesButton,
            inboxHeader: styles.inboxHeader,
            inboxStatus__title: styles.inboxStatusTitle,
            notificationImageLoadingFallback:
              styles.notificationImageLoadingFallback,
            inbox__popoverContent: styles.inboxPopoverContent,
            notification: styles.notification,
            notificationList: styles.notificationList,
            notificationListContainer: styles.notificationListContainer,
          },
          baseTheme: {
            variables: {
              colorPrimary: "var(--runn-blue)",
              colorBackground: "white",
            },
          },
        }}
        renderBell={(unseenCount) => (
          <div className={styles.bell} onClick={handleBellClick}>
            <Icon icon="notifications" size={20} color="var(--winter)" />
            {unseenCount > 0 && <div className={styles.unseenCountDot} />}
          </div>
        )}
        renderBody={(notification) => (
          <div
            className={styles.notificationBox}
            dangerouslySetInnerHTML={{
              __html: parseMentions(notification),
            }}
          />
        )}
        open={isInboxOpen}
        onNotificationClick={handleOnNotificationClick}
      ></Inbox>
    </div>
  )
}

const NotificationsWithErrorBoundary = (props: Props) => {
  return (
    <FallBackErrorBoundary fallback={null}>
      <Notifications {...props} />
    </FallBackErrorBoundary>
  )
}

export default withRelayPageContainerNoLoading(NotificationsWithErrorBoundary)
